import React, { Dispatch } from "react"
import { Box, Text } from "@chakra-ui/react"
import {
  BuilderBlockWithBlockId,
  FixedBlockType,
  Form,
  FormType,
} from "sharedTypes"
import { BlockInsertPosition } from "components/Builder/BuildingBlock"
import { DraggableItem } from "sharedTypes/builder"
import FormSubmitButton from "pages/EventShow/Forms/FormManagement/SelectedForm/FormSubmitButton"
import { formIconAndType, generateSubmitButtonText } from "utilities/forms"
import PaymentFieldsBlock from "../FormPaymentFields"
import FormPlusOnes from "../FormPlusOnes/FormPlusOnes"
import Blocks from "../Blocks"
import FixedBlock from "../FixedBlock"
import FormSection from "../../FormSection"
import FormHeader from "../FormHeader"
import TopFixedBlock from "./TopFixedBlock"

type Props = {
  form: Form
  blocks: BuilderBlockWithBlockId[]
  setBlocks: Dispatch<React.SetStateAction<BuilderBlockWithBlockId[]>>
  onDeleteBlock: () => void
  onDuplicateBlock: (blockId: number) => void
  portalPrimaryColor: string
  editable?: boolean
  setSelectedBlockId?: (blockId: number) => void
  onSelectFixedBlockType?: (blockType: FixedBlockType|undefined) => void
  selectedBlock?: BuilderBlockWithBlockId
  selectedFixedBlockType?: FixedBlockType
  onDropBlock?: (item: DraggableItem, blockId: number, position: BlockInsertPosition) => void
  formHeaderIsSeparate?: boolean
  mergeTagEventTitle?: string

}

const FormContainer = ({
  form,
  blocks,
  setBlocks,
  onDeleteBlock,
  onDuplicateBlock,
  portalPrimaryColor,
  editable,
  setSelectedBlockId,
  onSelectFixedBlockType,
  selectedBlock,
  selectedFixedBlockType,
  onDropBlock,
  formHeaderIsSeparate,
  mergeTagEventTitle,
}: Props) => {
  const {
    type,
    name,
    translations: {
      rsvpConfirm,
      rsvpDecline,
      rsvpPlusOneLabel,
      payment,
    },
    description,
  } = form

  const handleSelectBlock = (blockId: number) => {
    setSelectedBlockId?.(blockId)
    onSelectFixedBlockType?.(undefined)
  }

  const submitButtonText = generateSubmitButtonText(form)

  return (
    <Box m="auto" position="relative">
      <FormHeader icon={formIconAndType(type).icon} isSeparate={formHeaderIsSeparate}>
        <Box>
          <Text fontSize="2xl" fontWeight="semibold" lineHeight="normal">{name}</Text>
          <Text>{description}</Text>
        </Box>
      </FormHeader>
      <Box backgroundColor="white">
        <TopFixedBlock
          form={form}
          editable={editable}
          selectedFixedBlockType={selectedFixedBlockType}
          onDropBlock={onDropBlock}
          rsvpConfirm={rsvpConfirm}
          rsvpDecline={rsvpDecline}
          portalPrimaryColor={portalPrimaryColor}
          onSelectFixedBlockType={onSelectFixedBlockType}
          mergeTagEventTitle={mergeTagEventTitle}
        />
        <Blocks
          blocks={blocks}
          editable={editable}
          setSelectedBlockId={handleSelectBlock}
          selectedBlock={selectedBlock}
          setBlocks={setBlocks}
          onDeleteBlock={onDeleteBlock}
          onDuplicateBlock={onDuplicateBlock}
          onDropInto={onDropBlock}
        />
        <Box data-testid="form-builder-bottom-fixed-block">
          {type === FormType.Ticket && (
          <FixedBlock
            editable={editable}
            selected={selectedFixedBlockType === FixedBlockType.Payment}
            onSelect={() => onSelectFixedBlockType?.(FixedBlockType.Payment)}
            onDropInto={onDropBlock}
            blockInsertPosition={BlockInsertPosition.Top}
          >
            <PaymentFieldsBlock
              submitButtonColor={portalPrimaryColor}
              submitButtonText={submitButtonText}
              headerText={payment}
            />
          </FixedBlock>
          )}
          {type === FormType.RSVP && (
          <FixedBlock
            editable={editable}
            selected={selectedFixedBlockType === FixedBlockType.PlusOnes}
            onSelect={() => onSelectFixedBlockType?.(FixedBlockType.PlusOnes)}
            onDropInto={onDropBlock}
            blockInsertPosition={BlockInsertPosition.Top}
          >
            <FormPlusOnes label={rsvpPlusOneLabel} form={form} />
          </FixedBlock>
          )}
          {type !== FormType.Ticket && (
          <FixedBlock
            editable={editable}
            selected={selectedFixedBlockType === FixedBlockType.FormSubmission}
            onSelect={() => onSelectFixedBlockType?.(FixedBlockType.FormSubmission)}
            onDropInto={onDropBlock}
            blockInsertPosition={BlockInsertPosition.Top}
            allowDropInto={!form.plusOnesEnabled}
          >
            <FormSection last>
              <FormSubmitButton
                color={portalPrimaryColor}
              >{submitButtonText}
              </FormSubmitButton>
            </FormSection>
          </FixedBlock>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default FormContainer
